import { SelectItem } from "components/Forms/FormSelect";
import {
  StateWasteStreamRegistrationsQuery,
  StateWasteStreamRegistrationStatus,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { prettyPrintDateMed } from "util/dates";

import { StatusBadge, statusLabel } from "./StatusBadge";

export type RegistrationRow = NonNullable<
  StateWasteStreamRegistrationsQuery["stateWasteStreamRegistrations"]
>["items"][number];

// Shared select items for the status field on the edit drawer and create
// dialog. Each carries a StatusBadge node so the select renders the same pill
// as the grid; `display` keeps the plain label for search/display-only text.
export const STATUS_SELECT_ITEMS: SelectItem[] = Object.values(
  StateWasteStreamRegistrationStatus
).map((value) => ({ value, display: statusLabel(value), node: <StatusBadge status={value} /> }));

function renderDate(value: string | null | undefined): string {
  return value ? prettyPrintDateMed(value) : "—";
}

// Columns capture no component scope, so they live at module level for a
// zero-cost stable reference (matches GeneratorStatusChanges' COLUMNS).
export const COLUMNS: OmnisearchGridColDef<RegistrationRow>[] = [
  {
    // The tenant is now selected at the page level (see index.tsx), so this
    // column is display-only context and generates no filter chip.
    field: "tenant",
    headerName: "Organization",
    flex: 0.5,
    sortable: false,
    filterable: false,
    valueGetter: (_, row) => row.tenantName,
  },
  {
    field: "facility",
    headerName: "Facility",
    flex: 0.6,
    sortable: false,
    filterKeyType: "wasteFacility",
    valueGetter: (_, row) => row.facility?.name ?? row.facilityId,
  },
  {
    field: "state",
    headerName: "State",
    flex: 0.3,
    sortable: true,
  },
  {
    field: "stateRegisteredId",
    headerName: "State Registered ID",
    flex: 0.4,
    sortable: true,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.35,
    sortable: true,
    filterKeyType: "enum",
    enumValues: Object.values(StateWasteStreamRegistrationStatus),
    ignoreStateAbbreviations: true,
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
    valueGetter: (_, row) => statusLabel(row.status),
  },
  {
    field: "name",
    headerName: "Name",
    flex: 0.6,
    sortable: true,
  },
  {
    field: "companyDescription",
    headerName: "Company Description",
    flex: 0.6,
    sortable: true,
  },
  {
    field: "federalWasteCodes",
    headerName: "Federal Waste Codes",
    flex: 0.5,
    sortable: false,
    valueGetter: (_, row) => row.federalWasteCodes.join(", "),
  },
  {
    field: "formCodes",
    headerName: "Form Codes",
    flex: 0.4,
    sortable: false,
    valueGetter: (_, row) => row.formCodes.join(", "),
  },
  {
    field: "sourceCode",
    headerName: "Source Code",
    flex: 0.3,
    sortable: false,
  },
  {
    field: "registeredAt",
    headerName: "Registered At",
    flex: 0.35,
    sortable: true,
    valueGetter: (_, row) => renderDate(row.registeredAt),
  },
  {
    field: "closedAt",
    headerName: "Closed At",
    flex: 0.35,
    sortable: true,
    valueGetter: (_, row) => renderDate(row.closedAt),
  },
  {
    field: "linkedWasteStreamCount",
    headerName: "Linked Streams",
    flex: 0.3,
    sortable: false,
    filterable: false,
    type: "number",
  },
  {
    // Filter-only: hidden by default in the grid (see columnVisibilityModel in
    // index.tsx) since the Linked Streams count already conveys this — but the
    // column stays defined so the "No linked Encamp stream" filter is still
    // generated. The resolver filters on the same computed condition
    // (`wasteStreams: { none: {} }`); the client valueGetter mirrors it.
    field: "noLinkedStream",
    headerName: "No linked Encamp stream",
    flex: 0.3,
    sortable: false,
    type: "boolean",
    filterKeyType: "boolean",
    valueGetter: (_, row) => row.linkedWasteStreamCount === 0,
  },
];

// These fields have no server-side omnisearch handling in
// staffStateWasteStreamRegistrations.ts (only tenantId, facilityId, state,
// status, and noLinkedStream are parsed) — excluding them avoids generating
// dead free-text filters in the Filters dialog.
export const EXCLUDE_FILTER_COLUMNS = [
  "stateRegisteredId",
  "name",
  "companyDescription",
  "federalWasteCodes",
  "formCodes",
  "sourceCode",
  "registeredAt",
  "closedAt",
  "linkedWasteStreamCount",
];
